import { createContext, useCallback, useContext, useMemo, useState } from 'react';

export const THEME_STORAGE_KEY = 'theme';

const ThemeContext = createContext(null);

import { SITE_CONFIG } from '../config/site';

// Theme is fixed via src/config/site.js - no user-facing toggle.
function readThemeFromDocument() {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', SITE_CONFIG.theme);
  }
  return SITE_CONFIG.theme;
}

function applyThemeToDocument(next) {
  const v = next === 'light' ? 'light' : 'dark';
  document.documentElement.setAttribute('data-theme', v);
  try {
    localStorage.setItem(THEME_STORAGE_KEY, v);
  } catch { /* storage unavailable */ }
  return v;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readThemeFromDocument);

  const setTheme = useCallback((next) => {
    const v = applyThemeToDocument(next);
    setThemeState(v);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((prev) => {
      const next = prev === 'dark' ? 'light' : 'dark';
      applyThemeToDocument(next);
      return next;
    });
  }, []);

  const value = useMemo(() => ({ theme, setTheme, toggleTheme }), [theme, setTheme, toggleTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return ctx;
}
